import {getCapabilityManagementFeatureStatus} from 'auth/capabilities/CapabilityFeatureStatus';
import BenchlingConfig from 'BenchlingConfig';

import {FeatureDefinition} from './FeatureConstants';

export enum DisabledReason {
  MISSING_CAPABILITY = 'MISSING_CAPABILITY',
  INSUFFICIENT_PERMISSION = 'INSUFFICIENT_PERMISSION',
  LEGACY = 'LEGACY',
}

interface EnabledStatus {
  enabled: true;
}

interface DisabledStatus {
  enabled: false;
  reason: DisabledReason;
  userMessage?: string;
}

export type FeatureStatus = EnabledStatus | DisabledStatus;

export function makeFeatureStatus(
  isEnabled: boolean,
  reason: DisabledReason,
  userMessage?: string
): FeatureStatus {
  if (isEnabled) {
    return {enabled: true};
  }
  return {
    enabled: false,
    reason,
    userMessage,
  };
}

/**
 * Gets the status of a single feature
 *
 * @param featureDefinition An object that defines the feature to be checked.
 * @returns A status object for the feature
 */
export function getFeatureStatus(featureDefinition: FeatureDefinition): FeatureStatus {
  return getCapabilityManagementFeatureStatus(featureDefinition);
}

/**
 * Determines whether a feature is enabled.
 *
 * e.g. isFeatureEnabled({feature: RegistryFeature.EXPORT}) => false
 *
 * Some features require checking an item's permissions. You can pass the item like this:
 *
 * isFeatureEnabled({feature: RegistryFeature.REGISTER, item: sequence}) => true
 *
 * Note that in this example, a mapping from RegistryFeature.REGISTER->"writable" needs to exist in
 * FEATURE_TO_PERMISSION_KEY.
 *
 * @param featureDefinition An object that defines the feature to be checked.
 * @returns True if feature enabled. False if not.
 */
export function isFeatureEnabled(featureDefinition: FeatureDefinition): boolean {
  return getFeatureStatus(featureDefinition).enabled;
}

export function isConnectFeatureEnabled(featureDefinition: FeatureDefinition): boolean {
  return getFeatureStatus(featureDefinition).enabled && BenchlingConfig.INSTRUMENTS_ENABLE;
}

// Used in tests
export type _isFeatureEnabledType = typeof isFeatureEnabled;

export function areAnyFeaturesEnabled(featureDefinitions: Array<FeatureDefinition>): boolean {
  return featureDefinitions.some((featureDefinition) => isFeatureEnabled(featureDefinition));
}

export function areAllFeaturesEnabled(featureDefinitions: Array<FeatureDefinition>): boolean {
  return featureDefinitions.every((featureDefinition) => isFeatureEnabled(featureDefinition));
}
